import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { invoicesApi } from '@/services/invoices'
import { getApiErrorMessage } from '@/services/api'
import { useQueryClient } from '@tanstack/react-query'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { INVOICE_MODE, type InvoiceBulkSendMode } from '@/lib/constants'
import type {
  BulkSendInvoicesResponse,
  Invoice,
  UnpaidByClientRow,
} from '@/types'

interface Props {
  open: boolean
  onOpenChange: (o: boolean) => void
  invoices: Invoice[]
  mode: InvoiceBulkSendMode
  // 2.6.6 UNPAID mode: the full overdue-by-client aggregate (all pages, Overdue
  // only). Drives the confirm preview; the actual send resolves the same set
  // server-side from the identical filter, so preview and send cannot diverge.
  unpaidRows?: UnpaidByClientRow[]
}

// 2.6.6 Bulk send invoices - confirmation with count + recipient list, then a
// per-invoice success/failure summary.
export function BulkSendInvoicesDialog({
  open,
  onOpenChange,
  invoices,
  mode,
  unpaidRows = [],
}: Props) {
  const { t } = useTranslation('billing')
  const qc = useQueryClient()
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<BulkSendInvoicesResponse | null>(null)
  // UNPAID mode is queued server-side, so it reports a count of invoices queued
  // rather than a per-invoice sent/failed result.
  const [queued, setQueued] = useState<number | null>(null)

  const isUnpaidMode = mode === INVOICE_MODE.UNPAID
  const done = result !== null || queued !== null

  // In UNPAID mode the preview reflects the tenant-wide overdue set (count +
  // distinct client recipients); in SELECTED mode it reflects the picked rows.
  const count = isUnpaidMode
    ? unpaidRows.reduce((sum, r) => sum + r.unpaid_count, 0)
    : invoices.length

  const recipients = isUnpaidMode
    ? unpaidRows.flatMap(row =>
        Array.from(
          { length: row.unpaid_count },
          () => row.client_name ?? t('bulkSend.unknownClient')
        )
      )
    : invoices.map(
        invoice =>
          invoice.client?.name ??
          invoice.client_name ??
          t('bulkSend.unknownClient')
      )

  const invoiceLabel = (id: string) => {
    const inv = invoices.find(i => i.id === id)
    return inv?.number ?? id.slice(0, 8)
  }

  const titleKey =
    mode === INVOICE_MODE.SELECTED ? 'bulkSend.titleSelected' : 'bulkSend.title'
  const confirmKey =
    mode === INVOICE_MODE.SELECTED
      ? 'bulkSend.confirmSelected'
      : 'bulkSend.confirm'

  const close = () => {
    setResult(null)
    setQueued(null)
    onOpenChange(false)
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
    qc.invalidateQueries({ queryKey: ['invoices', 'summary'] })
    qc.invalidateQueries({ queryKey: ['invoices', 'unpaid-by-client'] })
    invalidatePortalMetricQueries(qc)
  }

  const confirm = async () => {
    if (count === 0) {
      toast({ variant: 'destructive', title: t('bulkSend.noUnpaid') })
      return
    }
    setSending(true)
    try {
      if (isUnpaidMode) {
        // UNPAID: server resolves the whole past-due set (all pages) and queues
        // it in chunks — the response only reports how many were queued.
        const res = await invoicesApi.bulkSendUnpaid()
        const n = res.data?.queued ?? 0
        setQueued(n)
        toast({ variant: 'success', title: t('bulkSend.queued', { count: n }) })
      } else {
        // SELECTED: send exactly the picked invoice ids (synchronous result).
        const res = await invoicesApi.bulkSend({
          invoice_ids: invoices.map(i => i.id),
        })
        const data = res.data ?? { sent: [], failed: [] }
        setResult(data)
        const failed = data.failed.length
        toast({
          variant: failed > 0 ? 'destructive' : 'success',
          title: t('bulkSend.summary', { sent: data.sent.length, failed }),
        })
      }
      invalidate()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('bulkSend.error')),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => (o ? onOpenChange(o) : close())}>
      <DialogContent className='max-w-[600px] gap-0 border-border p-8 sm:rounded-lg'>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-3 pr-8'>
            <DialogTitle className='text-lg font-semibold'>
              {t(titleKey)}
            </DialogTitle>
            {queued !== null ? (
              <div className='space-y-2 text-base leading-[1.4] text-foreground'>
                <p>{t('bulkSend.queued', { count: queued })}</p>
                <p className='text-sm text-muted-foreground'>
                  {t('bulkSend.queuedHint')}
                </p>
              </div>
            ) : result ? (
              <div className='space-y-3 text-base leading-[1.4] text-foreground'>
                <p>
                  {t('bulkSend.summary', {
                    sent: result.sent.length,
                    failed: result.failed.length,
                  })}
                </p>
                {result.failed.length > 0 && (
                  <div className='space-y-2'>
                    <p className='font-medium text-destructive'>
                      {t('bulkSend.failedDetails')}
                    </p>
                    <ul className='max-h-48 space-y-2 overflow-auto rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm'>
                      {result.failed.map(item => (
                        <li key={item.id}>
                          <span className='font-medium'>
                            {invoiceLabel(item.id)}
                          </span>
                          <span className='text-muted-foreground'> — </span>
                          <span className='text-destructive'>
                            {item.reason}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className='space-y-2 text-base leading-[1.4] text-foreground'>
                <p>
                  <Trans
                    t={t}
                    i18nKey='bulkSend.intro'
                    values={{ count }}
                    components={[<strong key='0' />]}
                  />
                </p>
                <ul className='max-h-40 list-inside list-disc overflow-auto text-sm text-muted-foreground'>
                  {recipients.map((recipient, index) => (
                    <li key={`${recipient}-${index}`}>{recipient}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={close} disabled={sending}>
              {done ? t('bulkSend.close') : t('bulkSend.cancel')}
            </Button>
            {!done && (
              <Button disabled={sending} onClick={confirm}>
                {sending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                {t(confirmKey)}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
